import { Button, Grid, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { ErrorIndicator } from 'components/Shared/ErrorIndicator'
import {
  startOfToday,
  format,
  subYears,
  startOfYear,
  endOfMonth,
  subMonths,
  endOfDay,
  startOfDay,
} from 'date-fns'
import { UseGetTrailingNtiTrends } from 'hooks/api/Reports/useGetTrailingNtiTrends'
import PlaceIcon from '@mui/icons-material/Place'
import PublicIcon from '@mui/icons-material/Public'
import { TrailingNtiTrendsChart } from './TrailingNtiTrendsChart'
import { useSetLe } from 'stores/useSetLe'
import { GamingReportsHeader } from '../GamingReportsHeader'
import { getLEIdsByPermission } from 'utils/permissions'
import { useCurrentCorporateAccountStore } from 'stores/useCurrentCorporateAccountStore'
import { useSetMe } from 'stores/useMeQuery'

export const TrailingNtiTrendsPage = () => {
  const { passedLe } = useSetLe()
  const { passedMe } = useSetMe()
  const meQuery = passedMe
  const selectedCorporateAccountId =
    useCurrentCorporateAccountStore().currentCorporateAccountId
  const today = startOfToday()
  const startDate = startOfDay(startOfYear(subYears(today, 3)))
  const endDate = endOfDay(endOfMonth(subMonths(today, 1)))

  // Get LE Ids for the permission the user has access to
  const leIdsByPermission: number[] = getLEIdsByPermission(
    meQuery.data,
    selectedCorporateAccountId,
    'gamingReportsPermission'
  )

  const reportQuery = UseGetTrailingNtiTrends({
    licensedEstablishmentId: Number(passedLe?.id),
    startDate,
    endDate,
    enabled: !!passedLe?.id,
  })

  const formattedStartDate = format(startDate, 'MM/dd/yyyy')
  const formattedEndDate = format(endDate, 'MM/dd/yyyy')

  const [displayType, setDisplayType] = useState<'Location' | 'Total'>(
    'Location'
  )

  const buttonStyle = (type: 'Location' | 'Total') => ({
    maxWidth: '40px',
    maxHeight: '40px',
    minWidth: '40px',
    minHeight: '40px',
    fontSize: '1.8rem',
    backgroundColor: displayType === type ? '#CC2027' : '#e0e0e0',
    color: displayType === type ? 'white' : 'black',
  })

  return (
    <GamingReportsHeader currentTab={'4'}>
      {leIdsByPermission.length !== 0 && (
        <>
          <Typography variant="h4">
            {`Last 3 Years: ${formattedStartDate} - ${formattedEndDate}`}
          </Typography>
          <Stack spacing={4}>
            <Grid container spacing={1} alignItems="center">
              <Grid item sx={{ mt: 5 }}>
                <Button
                  variant="contained"
                  onClick={() => setDisplayType('Location')}
                  style={buttonStyle('Location')}
                  title="Location NTI"
                >
                  <PlaceIcon />
                </Button>
              </Grid>
              <Grid item sx={{ mt: 5 }}>
                <Button
                  variant="contained"
                  onClick={() => setDisplayType('Total')}
                  style={buttonStyle('Total')}
                  title="Total NTI"
                >
                  <PublicIcon />
                </Button>
              </Grid>
            </Grid>

            {reportQuery.isPending && reportQuery.isFetching && (
              <ActivityIndicator />
            )}
            {reportQuery.isError && <ErrorIndicator />}
            {reportQuery.data &&
              (reportQuery.data.metrics.length === 0 ? (
                <Typography variant="h4"></Typography>
              ) : (
                <TrailingNtiTrendsChart
                  data={reportQuery.data.metrics}
                  displayType={displayType}
                />
              ))}
          </Stack>
        </>
      )}
    </GamingReportsHeader>
  )
}
